import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConsultantTutorialStep,
  ConsultantTutorialStepDetailsResponse,
  fetchConsultantTutorialStepDetails,
  patchConsultantTutorialActive,
  patchConsultantTutorialStep,
  TutorialStatus,
} from '../../shared/api/api';
import analytics from '../../shared/services/analytics/analytics';
import { logErrorService } from '../../shared/services';

export interface BaseTutorialStore {
  isTutorialExpired: boolean;
  isTutorialModeActive: boolean;
  currentlyViewedStep: number | null;
  tutorialTotalSteps: number;
  status: TutorialStatus;
  steps: ConsultantTutorialStep[];
}

export interface BaseTutorialStoreProps {
  tutorial: BaseTutorialStore;
}

const initialState: BaseTutorialStore = {
  isTutorialExpired: false,
  isTutorialModeActive: false,
  currentlyViewedStep: 0,
  tutorialTotalSteps: 5,
  status: 'LOADING',
  steps: [],
};

//start State:
// const initialState: BaseTutorialStore = {
//   isTutorialExpired: false,
//   isTutorialModeActive: false,
//   currentlyViewedStep: 0,
//   maxUnlockedTutorialStep: 0,
//   tutorialStartDate: null,
//   tutorialTotalSteps: 5,
// };

//expired State:
// const initialState: BaseTutorialStore = {
//   isTutorialExpired: true,
//   isTutorialModeActive: false,
//   currentlyViewedStep: 2,
//   maxUnlockedTutorialStep: 4,
//   tutorialStartDate: '2025-01-01',
//   tutorialTotalSteps: 5,
// };

// final State:
// const initialState: BaseTutorialStore = {
//   isTutorialExpired: false,
//   isTutorialModeActive: false,
//   currentlyViewedStep: 4,
//   maxUnlockedTutorialStep: 4,
//   tutorialStartDate: '2025-02-01',
//   tutorialTotalSteps: 5,
// };

export const ConsultantTutorialSlice = createSlice({
  name: 'ConsultantTutorial',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    advanceTutorial(state, action: PayloadAction<number>) {
      const stepIndex = action.payload;
      //if the new maxUnlockedTutorialStep is not loaded yet, advance to the next step
      const newStep = Math.min(stepIndex + 1, state.tutorialTotalSteps - 1);
      state.currentlyViewedStep = newStep;
      patchConsultantTutorialStep(newStep);
    },
    setNextStep(state) {
      const currentlyViewedStep = state.currentlyViewedStep ?? 0;
      analytics.trackCustomEvent('consultant_tutorial_click_next', { event_label: currentlyViewedStep.toString() });

      const newStep = Math.min(currentlyViewedStep + 1, state.tutorialTotalSteps - 1);
      state.currentlyViewedStep = newStep;
      patchConsultantTutorialStep(newStep);
    },
    setPreviousStep(state) {
      const currentlyViewedStep = state.currentlyViewedStep ?? 0;
      analytics.trackCustomEvent('consultant_tutorial_click_previous', { event_label: currentlyViewedStep.toString() });
      if (currentlyViewedStep <= 0) {
        return;
      }
      const newStep = currentlyViewedStep - 1;
      state.currentlyViewedStep = newStep;
      patchConsultantTutorialStep(newStep);
    },
    setState(state, action: PayloadAction<ConsultantTutorialStepDetailsResponse>) {
      const payload = action.payload;
      return {
        ...state,
        isTutorialExpired: false, // Consultant tutorial doesn't have an expired state
        isTutorialModeActive: payload.active,
        currentlyViewedStep: payload.currentStep,
        tutorialTotalSteps: payload.steps.length,
        status: payload.status,
        steps: payload.steps,
      };
    },
    enableTutorialMode(state) {
      //if the tutorial was not started, another action is needed (startTutorial)
      state.isTutorialModeActive = true;
      patchConsultantTutorialActive(true);
    },
    disableTutorialMode(state) {
      state.isTutorialModeActive = false;
      patchConsultantTutorialActive(false);
    },
    finishTutorial(state) {
      analytics.trackCustomEvent('consultant_tutorial_click_finish');
      state.isTutorialModeActive = false;
      state.currentlyViewedStep = 0;
      patchConsultantTutorialActive(true);
    },
    resetTutorialState(state) {
      state.isTutorialExpired = false;
      state.currentlyViewedStep = 0;
    },
  },
});

export const ConsultantTutorialActions = ConsultantTutorialSlice.actions;
export const setInitialConsultantTutorialState = () => async (dispatch: any) => {
  fetchConsultantTutorialStepDetails()
    .then(tutorialState => {
      if (tutorialState) {
        dispatch(ConsultantTutorialActions.setState(tutorialState));
      }
    })
    .catch(error => {
      console.error('Error fetching consultant tutorial step details:', error);
      logErrorService(error);
    });
};

// update the Redux state when the API response is received
export const updateConsultantTutorialState = (tutorialState: ConsultantTutorialStepDetailsResponse) => (dispatch: any) => {
  dispatch(ConsultantTutorialActions.setState(tutorialState));
};

export const consultantTutorialReducer = ConsultantTutorialSlice.reducer;
